import { useParams } from 'react-router-dom'
import { documentationRootGuid, projectFilesRootGuid } from '@constants'
import produce from 'immer'
import { findIndex } from 'lodash-es'
import { useMutation } from '@tanstack/react-query'
import { useSnackbar } from '@ui/snackbar'
import { getProjectFiles, getProjectFilesTemplates } from '@queries'
import { postFileMetadata } from '@mutations'
import queryClient from 'src/state/client'

const useRenameDocument = (rootGuid: string) => {
  const { projectId } = useParams()
  const { enqueueSnackbar } = useSnackbar()

  return useMutation(
    async ({ file, name }: { file: FileItem; name: string }) => {
      const config = {
        ...(file.additional_data ?? {}),
        name,
        item_guid: file.guid,
      } as FileItemConfig

      return await postFileMetadata.request(projectId, rootGuid, config)
    },
    {
      onMutate: async ({ file, name }) => {
        const filesKey = getProjectFiles.getKey(projectId, rootGuid)

        await queryClient.cancelQueries(filesKey)

        const previousFiles = queryClient.getQueryData<FilesResult>(filesKey)

        queryClient.setQueryData<FilesResult>(filesKey, prev => {
          if (!prev) return prev

          return produce(prev, draft => {
            const index = findIndex(draft.files, f => f.guid === file.guid)
            if (index !== -1) {
              draft.files[index].name = name
            }
          })
        })

        return { filesKey, previousFiles }
      },
      onSuccess: () => {
        queryClient.invalidateQueries(getProjectFiles.getKey(projectId, rootGuid))
        if (rootGuid === projectFilesRootGuid) {
          // The "Dateien" templates show the project files' names
          queryClient.invalidateQueries(
            getProjectFilesTemplates.getKey(projectId, documentationRootGuid),
          )
        }
      },
      onError: (_error, _variables, context) => {
        if (context?.previousFiles) {
          queryClient.setQueryData(context.filesKey, context.previousFiles)
        }
        enqueueSnackbar('Fehler beim Umbenennen', { variant: 'error' })
      },
    },
  )
}

export default useRenameDocument
